function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import ServiceFactory from "../../service";
import { INPUT } from "../../components/form-styles";

// Narration voices (UX §406, 2026-10-05): one catalog for Create, Make and the Builder, loaded once from
// GET /content/voices (Polly neural + Gemini TTS, each with a shared ~6 s sample) and cached here for the page
// life. Falls back to the list the views used to hard-code when the endpoint is missing or down, so the picker
// never goes empty. The stored value stays the plain voice id string ("Joanna", "gemini:Kore").

export const STATIC_VOICES = [["Joanna", "Joanna · US, warm"], ["Matthew", "Matthew · US, deep"], ["Ruth", "Ruth · US, bright"], ["Stephen", "Stephen · US, calm"], ["Amy", "Amy · UK"], ["Brian", "Brian · UK"], ["Olivia", "Olivia · Australian"], ["gemini:Kore", "Kore · natural (Gemini)"], ["gemini:Puck", "Puck · upbeat (Gemini)"]];
function svc(api) {
  const S = ServiceFactory.getType(api, "product");
  return new S();
}
const toast = m => {
  const t = document.querySelector("paper-toast");
  if (t && m) t.show(m);
};
const toStatic = () => STATIC_VOICES.map(([id, label]) => ({
  id,
  name: id.replace(/^gemini:/, ""),
  label,
  engine: id.startsWith("gemini:") ? "gemini" : "neural",
  sampleUrl: null,
  sampleSeconds: null
}));
let _catalog = null; // resolved list of { id, name, label, ..., sampleUrl }
let _loading = null; // in-flight promise (one request per page)
let _fromApi = false;

/** The catalog, loaded once; always resolves (static fallback on any failure). */
export function loadVoices() {
  if (_catalog && _fromApi) return Promise.resolve(_catalog);
  if (_loading) return _loading;
  _loading = (async () => {
    try {
      const r = await svc("/content/voices").getAsync();
      const items = r && (r.items || r.Items) || [];
      if (items.length) {
        _catalog = items.map(v => ({
          ...v,
          id: v.id || v.Id,
          label: v.label || v.Label || v.id || v.Id
        }));
        _fromApi = true;
      }
    } catch (e) {/* 404 or down: the static list below */}
    if (!_catalog) {
      _catalog = toStatic();
      _loading = null;
    } // let a later mount retry the endpoint
    return _catalog;
  })();
  return _loading;
}
export const voicesFromApi = () => _fromApi;
export const voiceList = () => _catalog || toStatic();
export const voiceLabel = id => {
  const v = voiceList().find(x => x.id === id);
  return v ? v.label : id;
};

// One shared <audio> for the whole page: pressing another sample stops the current one.
let _audio = null;
let _current = null; // key of what is playing
const _listeners = new Set();
function audio() {
  if (_audio) return _audio;
  _audio = new Audio();
  _audio.preload = "none";
  // "pause" is dispatched asynchronously, so a pause() issued while switching samples can land after the next
  // play() started: only clear when the element really is stopped.
  const done = () => {
    if (!_audio.paused && !_audio.ended) return;
    _current = null;
    _emit();
  };
  _audio.addEventListener("ended", done);
  _audio.addEventListener("pause", done);
  _audio.addEventListener("error", () => {
    _current = null;
    _emit();
  });
  return _audio;
}
function _emit() {
  _listeners.forEach(fn => {
    try {
      fn(_current);
    } catch (e) {/* listener gone */}
  });
}
export function stopAudio() {
  if (_audio) {
    try {
      _audio.pause();
    } catch (e) {/* no-op */}
  }
  _current = null;
  _emit();
}
/** Play a url under a key; playing the same key again stops it. Returns true when playback started. */
export async function playAudio(key, url) {
  if (!url) return false;
  if (_current === key) {
    stopAudio();
    return false;
  }
  const a = audio();
  try {
    a.pause();
  } catch (e) {/* no-op */}
  a.src = url;
  _current = key;
  _emit();
  try {
    await a.play();
    return true;
  } catch (e) {
    _current = null;
    _emit();
    toast("Could not play the sample.");
    return false;
  }
}
export const playingKey = () => _current;
export function subscribePlaying(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/** POST /content/voices/preview { voice, text } → { url, seconds }; throws on failure (429 → friendly message). */
export async function previewLine(voice, text) {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t) throw new Error("Nothing to preview yet.");
  if (t.length > 300) throw new Error("Preview the first 300 characters: shorten the line or preview a scene.");
  try {
    return await svc("/content/voices/preview").postAsync({
      voice,
      text: t
    });
  } catch (e) {
    const status = e && (e.status || e.statusCode || e.response && e.response.status);
    if (status === 429) throw new Error("Too many previews: try again in a minute.");
    if (status === 404) throw new Error("Previews are not available on this server yet.");
    throw new Error(e && e.message || "Could not make the preview.");
  }
}

/**
 * Voice select plus a Play / Stop chip for its sample. Props: value (voice id), onChange(id), disabled,
 * label ("Voice"), labelClass (e.g. "hub-bld-field"), selectStyle (defaults to INPUT), hint (optional <i> after
 * the label text). Keeps the parent's `voice` string state shape.
 */
export class VoicePicker extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      items: voiceList(),
      loading: !_catalog,
      playing: _current
    });
  }
  componentDidMount() {
    this._alive = true;
    this._unsub = subscribePlaying(k => {
      if (this._alive) this.setState({
        playing: k
      });
    });
    loadVoices().then(items => {
      if (this._alive) this.setState({
        items,
        loading: false
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._unsub) this._unsub();
  }
  render() {
    const {
      value,
      onChange,
      disabled,
      label = "Voice",
      labelClass,
      selectStyle,
      hint
    } = this.props;
    const {
      items,
      loading,
      playing
    } = this.state;
    const cur = items.find(v => v.id === value) || null;
    const key = `voice:${value}`;
    const isPlaying = playing === key;
    const canPlay = !!(cur && cur.sampleUrl) && !loading;
    return /*#__PURE__*/React.createElement("label", {
      className: labelClass
    }, label, hint ? /*#__PURE__*/React.createElement("i", null, hint) : null, /*#__PURE__*/React.createElement("span", {
      className: "hub-voice-row"
    }, /*#__PURE__*/React.createElement("select", {
      value: value,
      disabled: disabled || loading,
      onChange: e => onChange(e.target.value),
      style: selectStyle || INPUT
    }, items.map(v => /*#__PURE__*/React.createElement("option", {
      key: v.id,
      value: v.id
    }, v.label)), !cur && value ? /*#__PURE__*/React.createElement("option", {
      value: value
    }, value) : null), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs hub-voice-play" + (isPlaying ? " on" : ""),
      disabled: !canPlay,
      title: loading ? "Loading voices" : canPlay ? isPlaying ? "Stop the sample" : `Hear ${cur.name || cur.id}` : "No sample for this voice",
      onClick: e => {
        e.preventDefault();
        if (isPlaying) stopAudio();else playAudio(key, cur.sampleUrl);
      }
    }, loading ? "Loading…" : isPlaying ? "▮▮ Stop" : "▶ Play")));
  }
}

/**
 * "Preview this line" chip: synthesizes `text` in `voice` through the preview endpoint and plays it through the
 * shared player. Props: voice, text, disabled, label ("Preview this line"), className (defaults to the row chip).
 */
export class PreviewLineButton extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      busy: false,
      playing: _current,
      url: null,
      forKey: null
    });
    _defineProperty(this, "go", async e => {
      if (e) e.preventDefault();
      const key = this.lineKey();
      if (this.state.playing === key) {
        stopAudio();
        return;
      }
      if (this.state.url && this.state.forKey === key) {
        playAudio(key, this.state.url);
        return;
      }
      this.setState({
        busy: true
      });
      try {
        const r = await previewLine(this.props.voice, this.props.text);
        const url = r && (r.url || r.Url);
        if (!url) throw new Error("Could not make the preview.");
        if (!this._alive) return;
        this.setState({
          url,
          forKey: key,
          busy: false
        });
        playAudio(key, url);
      } catch (err) {
        if (this._alive) this.setState({
          busy: false
        });
        toast(err && err.message);
      }
    });
  }
  componentDidMount() {
    this._alive = true;
    this._unsub = subscribePlaying(k => {
      if (this._alive) this.setState({
        playing: k
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._unsub) this._unsub();
  }
  lineKey() {
    return `line:${this.props.voice}:${String(this.props.text || "").replace(/\s+/g, " ").trim()}`;
  }
  render() {
    const {
      disabled,
      text,
      label = "Preview this line",
      className = "territory-toggle xs"
    } = this.props;
    const has = !!String(text || "").trim();
    const isPlaying = this.state.playing === this.lineKey();
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: className + (isPlaying ? " on" : ""),
      disabled: disabled || this.state.busy || !has,
      title: has ? "Hear this line in the chosen voice before rendering" : "Write the line first",
      onClick: this.go
    }, this.state.busy ? "Making…" : isPlaying ? "▮▮ Stop" : label);
  }
}